/**
 * Opening guidance, for the handful of moves where principles beat calculation.
 *
 * Connect 4's opening theory is short. Unlike chess there are no long named
 * variations to memorise; a few ideas about where the value on the board is
 * cover the first eight or so discs, after which the position is concrete and
 * the search has more to say than any principle.
 *
 * Two kinds of claim live here, and they are kept apart on purpose:
 *
 *  - **Derived.** "This square sits in more winning lines than any other" is
 *    counted off the spec's own win conditions, so it is true for whatever game
 *    the spec describes and stays true if someone authors a different board.
 *  - **Cited.** Connect 4 was solved by exhaustive computer analysis: on the
 *    standard 7x6 board the first player forces a win by opening in the centre
 *    column, the two columns beside it draw, and the remaining four lose.
 *    That is a fact about one specific game, not something this module can
 *    prove, so it is stated only when the spec really is that game.
 *
 * Anything that is neither derived nor cited is left unsaid. A learner cannot
 * tell an invented principle from a real one, so inventing even one devalues
 * the rest.
 */
import { boardSize, indexOf } from '../spec/interpreter.ts';
import type { State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { phaseOf } from './coach.ts';
import { enumerateWinWindows, longestWinLength } from './eval.ts';

/** One piece of opening advice, ready to be turned into a sentence. */
export interface OpeningAdvice {
  /**
   * Stable identifier, so the UI can avoid repeating itself and tests can
   * assert which advice fired without matching prose.
   */
  readonly id:
    /** Nothing on the board yet. */
    | 'take_centre'
    /** The centre column is empty and still the best square available. */
    | 'contest_centre'
    /** The centre column already has discs in it but still leads. */
    | 'stack_centre'
    /** Some other column now offers the most, so the centre is spoken for. */
    | 'centre_is_taken';
  /** Columns the advice points at. The UI may ghost these on the board. */
  readonly params: readonly number[];
  /** How many winning lines run through the recommended landing square. */
  readonly lines: number;
  /**
   * The fewest any currently playable square reaches.
   *
   * The comparison *is* the lesson. "Seven winning lines" means nothing on its
   * own; "seven, where the edges manage three" is the whole argument for the
   * centre in one clause, and it is counted rather than asserted.
   */
  readonly fewest: number;
  /**
   * True when the spec is standard Connect 4 and the solved-game result may be
   * quoted. False for every authored variant, which keeps the citation honest.
   */
  readonly solvedGame: boolean;
}

/**
 * Windows through every cell, cached per spec.
 *
 * Enumerating windows walks the whole board for each condition and direction.
 * The coach asks for advice on every turn of every practice game, and they all
 * share one frozen spec object, so recomputing would be the most expensive part
 * of a module whose entire job is to be cheaper than the search.
 */
const REACH_CACHE = new WeakMap<RulesSpec, readonly number[]>();

/** How many winning lines pass through each board cell. */
export function cellReach(rules: RulesSpec): readonly number[] {
  const cached = REACH_CACHE.get(rules);
  if (cached) return cached;

  const counts = new Array<number>(boardSize(rules)).fill(0);
  for (const window of enumerateWinWindows(rules)) {
    for (const cell of window) {
      counts[cell] = (counts[cell] ?? 0) + 1;
    }
  }

  const frozen = Object.freeze(counts);
  REACH_CACHE.set(rules, frozen);
  return frozen;
}

/** The cell a disc dropped in `col` would land in, or -1 when the column is full. */
export function landingCell<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  col: number,
): number {
  for (let row = 0; row < rules.board.rows; row += 1) {
    const cell = indexOf(rules, row, col);
    if (state.board[cell] === rules.cells.empty) return cell;
  }
  return -1;
}

/**
 * True when this spec is the game the solved-game literature is about.
 *
 * Deliberately strict. A 7x6 board needing five in a row, or one with three
 * seats, is a different game whose opening theory nobody has published, and
 * quoting Connect 4's result at it would be a confident falsehood.
 */
export function isStandardConnect4(rules: RulesSpec): boolean {
  return (
    rules.board.cols === 7 &&
    rules.board.rows === 6 &&
    rules.seats.length === 2 &&
    longestWinLength(rules) === 4 &&
    rules.terminal.win.every((condition) => condition.length === 4)
  );
}

/** Columns ordered by how many winning lines their landing square sits in. */
function rankColumns<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): readonly { readonly col: number; readonly lines: number }[] {
  const reach = cellReach(rules);
  const ranked: { col: number; lines: number }[] = [];
  for (let col = 0; col < rules.board.cols; col += 1) {
    const cell = landingCell(rules, state, col);
    if (cell === -1) continue;
    ranked.push({ col, lines: reach[cell] ?? 0 });
  }
  // Ties break towards the lower column so the advice is deterministic.
  return ranked.sort((a, b) => b.lines - a.lines || a.col - b.col);
}

/** How many discs are on the board. */
function discCount<S extends RulesSpec>(rules: S, state: State<S>): number {
  const size = boardSize(rules);
  let filled = 0;
  for (let index = 0; index < size; index += 1) {
    if (state.board[index] !== rules.cells.empty) filled += 1;
  }
  return filled;
}

/**
 * Advice for the position, or null once the opening is over.
 *
 * Null is the common case after a few moves and is not a failure: past the
 * opening the honest thing to point at is the concrete tactics `readPosition`
 * finds, not a maxim that happens to still be grammatical.
 */
export function openingAdvice<S extends RulesSpec>(
  rules: S,
  state: State<S>,
): OpeningAdvice | null {
  if (state.status.kind !== 'in_play') return null;

  // One definition of "the opening", shared with the coach, so advice cannot
  // stop a move before or after the phase it belongs to.
  if (phaseOf(rules, state) !== 'opening') return null;
  const discs = discCount(rules, state);

  const ranked = rankColumns(rules, state);
  const top = ranked[0];
  const tail = ranked[ranked.length - 1];
  if (top === undefined || tail === undefined) return null;

  const solvedGame = isStandardConnect4(rules);
  const shape = { lines: top.lines, fewest: tail.lines, solvedGame };

  // An empty board: the whole of the first move is "take the busiest square".
  if (discs === 0) return { id: 'take_centre', params: [top.col], ...shape };

  /*
   * Whether the centre column still offers the most.
   *
   * Compared by column rather than by line count, because the interesting event
   * is a change of *which column leads*. A disc in the centre does not end the
   * centre's claim: the square on top of it usually still beats everything on
   * the wings, which is why playing straight back on top is a real answer to a
   * centre opening rather than a wasted move.
   */
  const centreCol = Math.floor(rules.board.cols / 2);
  if (top.col !== centreCol) {
    return { id: 'centre_is_taken', params: [top.col], ...shape };
  }

  // An empty centre column is a different lesson from a contested one: one is
  // "they left the best square on the board", the other is "keep the column".
  const centreEmpty = landingCell(rules, state, centreCol) === indexOf(rules, 0, centreCol);
  return centreEmpty
    ? { id: 'contest_centre', params: [top.col], ...shape }
    : { id: 'stack_centre', params: [top.col], ...shape };
}
